import {
	AlertManager,
	useAlerts,
	useSynchronizedRef
} from "client/Hooks";
import { MailMessageLocationQueryModel } from "client/Api/Email/common";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { HttpRequestError } from "client/Api/httpClient";
import {
	ReadonlyRefObject,
	useCallback,
	useMemo
} from "react";
import { DraftEmailAttachment } from "client/Api/Email/respondToEmail";
import {
	uploadAttachment,
	UploadAttachmentErrorModel,
	UploadingFinishedAttachmentModel
} from "client/Api/Email/email";
import {
	AttachmentItem,
	AttachmentKind,
	AttachmentStatus,
	EnqueueAttachmentUpload
} from "./common";

export type UploadAttachmentsFromDeviceHandlers = {
	uploadAttachmentsFromDevice(...files: readonly File[]): Promise<void>;
};

export type UploadAttachmentsFromDeviceOptions = {
	draft: MailMessageLocationQueryModel;
	sizeLimit: number;
	totalVirtualSize: number;
	enqueueUpload: EnqueueAttachmentUpload;
	onAddAttachment(attachment: AttachmentItem): void;
	onAttachmentUploadFinish(uid: string, model: DraftEmailAttachment): void;
	onRemoveAttachment(uid: string): void;
};

export function useUploadAttachmentsFromDevice(options: UploadAttachmentsFromDeviceOptions): UploadAttachmentsFromDeviceHandlers {
	const {
		draft,
		sizeLimit,
		totalVirtualSize,
		enqueueUpload,
		onAddAttachment,
		onAttachmentUploadFinish,
		onRemoveAttachment
	} = options;

	const totalVirtualSizeRef: ReadonlyRefObject<number> = useSynchronizedRef<number>(totalVirtualSize);
	const alertManager: AlertManager = useAlerts();

	const uploadAttachmentFromDeviceHandler = useCallback(
		async (
			file: File,
			totalSize: number
		): Promise<void> => {
			const uid: Guid = newGuid();

			if (totalSize + file.size > sizeLimit) {
				alertManager.showError(localize("_OneOrMoreAttachmentsAreToBigToUpload"));
				return;
			}

			onAddAttachment(
				{
					status: AttachmentStatus.Uploading,
					kind: AttachmentKind.Normal,
					name: file.name,
					size: file.size,
					uid
				}
			);

			try {
				const uploadResult: UploadingFinishedAttachmentModel = await enqueueUpload(
					() => uploadAttachment(
						{
							draft,
							fileUid: uid
						},
						file
					)
				);

				onAttachmentUploadFinish(uid, uploadResult.attachment);
				return;
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				onRemoveAttachment(uid);

				let wasHandled: boolean = false;
				if (e instanceof HttpRequestError && e.statusCode === 400) {
					try {
						const errorModel = e.readJson<UploadAttachmentErrorModel>();

						switch (errorModel.errorReason) {
							case "AttachmentsTooBig":
								alertManager.showError(localize("_OneOrMoreAttachmentsAreToBigToUpload"));
								wasHandled = true;
								break;
							case "UserTryToUploadFolder":
								alertManager.showError(localize("_OneOrMoreFoldersAreUploaded"));
								wasHandled = true;
								break;
							default:
								console.error(`Unsupported error reason "${errorModel.errorReason}"`);
								break;
						}
					} catch(e) {
						console.error(e);
					}
				}

				if (!wasHandled) {
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}
		},
		[
			draft,
			sizeLimit,
			alertManager,
			enqueueUpload,
			onAddAttachment,
			onAttachmentUploadFinish,
			onRemoveAttachment
		]
	);

	const uploadAttachmentsFromDeviceHandler = useCallback(
		async (...files: readonly File[]): Promise<void> => {
			const allUploads: Promise<void>[] = [];

			let totalSizeOfCurrentUpload: number = 0;

			for (const file of files) {
				const upload: Promise<void> = uploadAttachmentFromDeviceHandler(
					file,
					totalVirtualSizeRef.current + totalSizeOfCurrentUpload
				);

				allUploads.push(upload);
				totalSizeOfCurrentUpload += file.size;
			}

			await Promise.all(allUploads);
		},
		[
			totalVirtualSizeRef,
			uploadAttachmentFromDeviceHandler
		]
	);

	return useMemo(
		(): UploadAttachmentsFromDeviceHandlers => ({
			uploadAttachmentsFromDevice: uploadAttachmentsFromDeviceHandler
		}),
		[
			uploadAttachmentsFromDeviceHandler
		]
	);
}